// Advanced Filter builder — operator/kind UI logic and shared types.
//
// The field catalogue itself (which INPACT fields are pickable, their
// labels/kinds/options) is served by the backend at GET /api/inpact/fields
// (see app/services/field_catalog.py) and fetched via the `useFields` hook —
// this file no longer hardcodes it. What stays here is purely client-side UI
// logic: which operators are valid for a given field kind, and how to label
// them, shared by the Advanced Filter builder's rule rows.
import type { FieldDef, FieldKind, FilterOperator } from "../types/inpact";

export type { FieldDef, FieldKind } from "../types/inpact";

/** Whether a catalogue entry may be authored in the filter builder. */
export function isFilterField(field: FieldDef): boolean {
  return field.available && field.capabilities.includes("filter");
}

// Ops permitted per field-kind. Wire format matches the backend planner.
//
// No IN / NOT IN for free text: the set-value editor is a MultiSelect built
// from the field's `options`, and a text field has none, so "is one of" would
// open an empty picker the user can never satisfy. Country keeps them because
// its options come from the /countries facet. This only became reachable when
// the catalogue turned CRM-driven — it now offers 15 Contact text fields
// (Email, Phone, LinkedIn…) where the old hand-written list had none.
const OPS_STRING: FilterOperator[] = [
  "=", "!=", "CONTAINS", "STARTS WITH", "IS NULL", "IS NOT NULL",
];
const OPS_COUNTRY: FilterOperator[] = [
  "=", "!=", "IN", "NOT IN", "CONTAINS", "STARTS WITH", "IS NULL", "IS NOT NULL",
];
const OPS_PICKLIST: FilterOperator[] = ["=", "!=", "IN", "NOT IN", "IS NULL", "IS NOT NULL"];
const OPS_MULTIPICKLIST: FilterOperator[] = ["INCLUDES", "EXCLUDES", "IS NULL", "IS NOT NULL"];
const OPS_BOOLEAN: FilterOperator[] = ["="];
const OPS_DATE: FilterOperator[] = [
  "=", "!=", ">", ">=", "<", "<=", "BETWEEN", "IS NULL", "IS NOT NULL",
];
const OPS_NUMBER: FilterOperator[] = [
  "=", "!=", ">", ">=", "<", "<=", "BETWEEN", "IS NULL", "IS NOT NULL",
];

export function opsForKind(kind: FieldKind): FilterOperator[] {
  switch (kind) {
    case "boolean": return OPS_BOOLEAN;
    case "date": return OPS_DATE;
    case "number": return OPS_NUMBER;
    case "picklist": return OPS_PICKLIST;
    case "multipicklist": return OPS_MULTIPICKLIST;
    case "country": return OPS_COUNTRY;
    case "string":
    default: return OPS_STRING;
  }
}

export const OP_LABELS: Record<FilterOperator, string> = {
  "=": "=",
  "!=": "≠",
  "IN": "is one of",
  "NOT IN": "is not one of",
  "INCLUDES": "includes",
  "EXCLUDES": "excludes",
  "<": "<",
  "<=": "≤",
  ">": ">",
  ">=": "≥",
  "BETWEEN": "between",
  "IS NULL": "is empty",
  "IS NOT NULL": "is not empty",
  "STARTS WITH": "starts with",
  "CONTAINS": "contains",
};
